import { useRef } from "react";
import { Box, Button, Card, TextField, Typography } from "@mui/material";
import { Trash2 } from "lucide-react";
import AccessPointFormFields from "../components/AccessPointFormFields";
import LayoutImageCanvas from "../components/LayoutImageCanvas";
import MapPicker from "../components/MapPicker";
import { AccessPoint, LayoutPin } from "../types";
import { passtrackColors } from "../theme";

interface AccessPointsStepProps {
  accessPoints: AccessPoint[];
  selectedPointId: string | null;
  onSelectPoint: (id: string) => void;
  onUpdatePoint: (id: string, patch: Partial<AccessPoint>) => void;
  onAddPoint: () => void;
  onRemovePoint: (id: string) => void;
  layoutImage: string;
  layoutPins: LayoutPin[];
  onLayoutImageChange: (value: string) => void;
  onLayoutPinsChange: (pins: LayoutPin[]) => void;
}

export default function AccessPointsStep({
  accessPoints,
  selectedPointId,
  onSelectPoint,
  onUpdatePoint,
  onAddPoint,
  onRemovePoint,
  layoutImage,
  layoutPins,
  onLayoutImageChange,
  onLayoutPinsChange,
}: AccessPointsStepProps) {
  const layoutInputRef = useRef<HTMLInputElement | null>(null);
  const selectedPoint =
    accessPoints.find((p) => p.id === selectedPointId) ??
    accessPoints[0] ??
    null;
  const selectedPointIndex = selectedPoint
    ? accessPoints.findIndex((p) => p.id === selectedPoint.id)
    : -1;

  const onLayoutFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      onLayoutImageChange(String(reader.result ?? ""));
      onLayoutPinsChange([]);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const clearLayout = () => {
    onLayoutImageChange("");
    onLayoutPinsChange([]);
  };

  const placePin = (
    accessPointId: string,
    xPercent: number,
    yPercent: number,
  ) => {
    const nextPin: LayoutPin = { accessPointId, xPercent, yPercent };
    const without = layoutPins.filter((p) => p.accessPointId !== accessPointId);
    onLayoutPinsChange([...without, nextPin]);
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1fr) 340px" },
        gap: 3,
        alignItems: "start",
      }}
    >
      <Card
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: 2.5,
          minHeight: { lg: 560 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography fontWeight={600} color={passtrackColors.ink}>
              Layout de la propiedad
            </Typography>
            <Typography sx={{ fontSize: 13, color: passtrackColors.textMuted }}>
              Sube un plano o imagen de referencia y coloca un pin por cada
              punto de acceso.
            </Typography>
          </Box>
          {layoutImage && (
            <Box
              component="button"
              type="button"
              onClick={clearLayout}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                background: "none",
                border: "none",
                color: passtrackColors.red,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <Trash2 size={14} />
              Quitar
            </Box>
          )}
        </Box>

        <LayoutImageCanvas
          image={layoutImage}
          pins={layoutPins}
          accessPoints={accessPoints}
          selectedPointId={selectedPoint?.id ?? null}
          onSelectPoint={onSelectPoint}
          onPlacePin={placePin}
          onUploadClick={() => layoutInputRef.current?.click()}
        />

        <input
          type="file"
          accept="image/*"
          ref={layoutInputRef}
          onChange={onLayoutFile}
          style={{ display: "none" }}
        />

        {layoutImage && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => layoutInputRef.current?.click()}
            sx={{ mt: 1.5, alignSelf: "flex-start" }}
          >
            Cambiar imagen
          </Button>
        )}
      </Card>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography fontWeight={600} color={passtrackColors.ink}>
              Puntos de acceso
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: passtrackColors.blue900,
                background: passtrackColors.cloud,
                borderRadius: 99,
                px: 1.25,
                py: 0.5,
              }}
            >
              {accessPoints.length} puntos
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {accessPoints.map((point, index) => {
              const on = point.id === selectedPoint?.id;
              const hasPin = layoutPins.some(
                (pin) => pin.accessPointId === point.id,
              );
              return (
                <Box
                  key={point.id}
                  onClick={() => onSelectPoint(point.id)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "26px 1fr auto",
                    gap: 1.5,
                    alignItems: "center",
                    textAlign: "left",
                    p: 1.5,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    background: on ? passtrackColors.cloud : "#fff",
                    border: `1px solid ${
                      on ? passtrackColors.blue : passtrackColors.borderSubtle
                    }`,
                  }}
                >
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: 99,
                      background: on
                        ? passtrackColors.blue
                        : passtrackColors.slate,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: passtrackColors.ink,
                      }}
                    >
                      {point.nombre || `Punto de acceso ${index + 1}`}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, color: passtrackColors.textMuted }}
                    >
                      {(point.calleOAvenida || "Sin avenida") +
                        " · " +
                        point.gates.length +
                        " puertas"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    {layoutImage && (
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: hasPin
                            ? passtrackColors.green700
                            : passtrackColors.textMuted,
                        }}
                      >
                        {hasPin ? "Pin" : "Sin pin"}
                      </Typography>
                    )}
                    {accessPoints.length > 1 && (
                      <Box
                        component="button"
                        type="button"
                        aria-label={`Eliminar ${point.nombre || `punto ${index + 1}`}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemovePoint(point.id);
                        }}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 0.5,
                          background: "none",
                          border: "none",
                          color: passtrackColors.red,
                          cursor: "pointer",
                          borderRadius: 1,
                          "&:hover": {
                            background: "rgba(211, 47, 47, 0.08)",
                          },
                        }}
                      >
                        <Trash2 size={14} />
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Button
            variant="outlined"
            onClick={onAddPoint}
            fullWidth
            sx={{ mt: 1.5 }}
          >
            Agregar punto de acceso
          </Button>
        </Card>

        {selectedPoint && (
          <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                mb: 2,
                gap: 2,
              }}
            >
              <Box>
                <Typography fontWeight={600} color={passtrackColors.ink}>
                  Detalle del punto
                </Typography>
                <Typography
                  sx={{ fontSize: 13, color: passtrackColors.textMuted }}
                >
                  Punto de Acceso {selectedPointIndex + 1}
                </Typography>
              </Box>
              {accessPoints.length > 1 && (
                <Box
                  component="button"
                  type="button"
                  onClick={() => onRemovePoint(selectedPoint.id)}
                  sx={{
                    background: "none",
                    border: "none",
                    color: passtrackColors.red,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  Eliminar
                </Box>
              )}
            </Box>

            <TextField
              fullWidth
              size="small"
              label="Nombre"
              value={selectedPoint.nombre}
              onChange={(e) =>
                onUpdatePoint(selectedPoint.id, { nombre: e.target.value })
              }
              placeholder="Ej. Acceso Principal Norte"
              required
              sx={{ mb: 2 }}
            />

            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{ fontSize: 12, color: passtrackColors.textMuted, mb: 1 }}
              >
                Haz clic en el mapa o arrastra el pin para ubicar el punto.
              </Typography>
              <MapPicker
                lat={selectedPoint.latitud}
                lng={selectedPoint.longitud}
                onChange={(lat, lng) =>
                  onUpdatePoint(selectedPoint.id, {
                    latitud: lat,
                    longitud: lng,
                  })
                }
                height={220}
              />
            </Box>

            <AccessPointFormFields
              point={selectedPoint}
              onUpdate={(patch) => onUpdatePoint(selectedPoint.id, patch)}
              hideName
            />
          </Card>
        )}
      </Box>
    </Box>
  );
}
